/**
 * Discovery types for token listing, search, filter, and sort.
 *
 * Used by GET /v1/tokens endpoint.
 * Indexer-enriched fields (price, volume, etc.) are nullable when indexer is unavailable.
 */

import { z } from "zod";

import { DataSourceSchema, TrendingTokenSchema } from "./indexer";
import { TokenCategorySchema, VerificationStatusSchema } from "./token";

// ─── Query Parameter Schemas ────────────────────────────────

export const TokenSortSchema = z.enum(["newest", "volume", "market_cap", "holders"]);
export type TokenSort = z.infer<typeof TokenSortSchema>;

export const SortOrderSchema = z.enum(["asc", "desc"]);
export type SortOrder = z.infer<typeof SortOrderSchema>;

export const ListTokensQuerySchema = z.object({
	search: z.string().max(100).optional(),
	category: TokenCategorySchema.optional(),
	sort: TokenSortSchema.default("newest"),
	order: SortOrderSchema.default("desc"),
	verification: VerificationStatusSchema.optional(),
	cursor: z.string().optional(),
	limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type ListTokensQueryParams = z.infer<typeof ListTokensQuerySchema>;

// ─── Response Types ─────────────────────────────────────────

export const TokenListItemSchema = z.object({
	id: z.string(),
	name: z.string(),
	symbol: z.string(),
	category: TokenCategorySchema,
	logoUrl: z.string().nullable(),
	verificationStatus: VerificationStatusSchema,
	createdAt: z.string(),
	// Indexer-enriched fields (nullable when indexer unavailable)
	price: z.number().nullable(),
	volume24h: z.number().nullable(),
	marketCap: z.number().nullable(),
	holderCount: z.number().nullable(),
	priceChange24h: z.number().nullable(),
	dataSource: DataSourceSchema,
});
export type TokenListItem = z.infer<typeof TokenListItemSchema>;

export const PaginatedTokenListSchema = z.object({
	items: z.array(TokenListItemSchema),
	pagination: z.object({
		nextCursor: z.string().nullable(),
		hasMore: z.boolean(),
	}),
});
export type PaginatedTokenList = z.infer<typeof PaginatedTokenListSchema>;

// ─── Trending Query & Response Types ─────────────────────────

export const GetTrendingQuerySchema = z.object({
	limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type GetTrendingQueryParams = z.infer<typeof GetTrendingQuerySchema>;

export const TrendingDataSourceSchema = z.object({
	dataSource: DataSourceSchema,
	stale: z.boolean().optional(),
});
export type TrendingDataSource = z.infer<typeof TrendingDataSourceSchema>;

export const TrendingTokensResponseSchema = z.object({
	items: z.array(TrendingTokenSchema),
	dataSource: TrendingDataSourceSchema,
});
export type TrendingTokensResponse = z.infer<typeof TrendingTokensResponseSchema>;
